import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { Plus, Trash, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const MASTERS = [
  {
    key: "branches",
    label: "Branches",
    fields: [
      { name: "name", label: "Name", type: "text", required: true },
      { name: "gst_number", label: "GST Number" },
      { name: "address", label: "Address" },
      { name: "phone", label: "Phone" },
      { name: "email", label: "Email" },
    ],
    columns: ["name", "gst_number", "phone", "email"],
  },
  {
    key: "categories",
    label: "Categories",
    fields: [
      { name: "name", label: "Name", type: "text", required: true },
      { name: "hsn_code", label: "HSN Code" },
      { name: "description", label: "Description" },
    ],
    columns: ["name", "hsn_code", "description"],
  },
  {
    key: "purities",
    label: "Purities",
    fields: [
      { name: "name", label: "Name (e.g. 22K)", type: "text", required: true },
      { name: "karat", label: "Karat", type: "number" },
      { name: "purity_factor", label: "Purity Factor (0-1)", type: "number" },
    ],
    columns: ["name", "karat", "purity_factor"],
  },
  {
    key: "metals",
    label: "Metals & Rates",
    fields: [
      { name: "name", label: "Name", type: "text", required: true },
      { name: "symbol", label: "Symbol" },
      { name: "current_rate", label: "Rate per gram (₹)", type: "number" },
    ],
    columns: ["name", "symbol", "current_rate"],
  },
];

function MasterTable({ def }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/${def.key}`);
      setRows(data);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
  }, [def.key]);

  const openCreate = () => {
    setEditing(null);
    const blank = {};
    def.fields.forEach((f) => (blank[f.name] = f.type === "number" ? 0 : ""));
    setForm(blank);
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({ ...row });
    setOpen(true);
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const payload = {};
      def.fields.forEach((f) => {
        let val = form[f.name];
        if (f.type === "number") val = Number(val) || 0;
        payload[f.name] = val;
      });
      if (editing) {
        await api.put(`/${def.key}/${editing.id}`, payload);
        toast.success(`${def.label.slice(0, -1)} updated`);
      } else {
        await api.post(`/${def.key}`, payload);
        toast.success(`${def.label.slice(0, -1)} created`);
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete ${row.name}?`)) return;
    try {
      await api.delete(`/${def.key}/${row.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-muted-foreground text-sm">
          {rows.length} {def.label.toLowerCase()}
        </div>
        <Button
          data-testid={`add-${def.key}-btn`}
          onClick={openCreate}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <Plus size={16} className="mr-1.5" /> Add
        </Button>
      </div>

      <div className="rounded-lg border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/40 hover:bg-secondary/40">
              {def.columns.map((c) => (
                <TableHead
                  key={c}
                  className="uppercase text-[10px] tracking-[0.18em]"
                >
                  {c.replace(/_/g, " ")}
                </TableHead>
              ))}
              <TableHead className="text-right uppercase text-[10px] tracking-[0.18em]">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={def.columns.length + 1}
                  className="text-center py-8 text-muted-foreground"
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={def.columns.length + 1}
                  className="text-center py-8 text-muted-foreground"
                >
                  No records
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.id} data-testid={`row-${def.key}-${r.id}`}>
                  {def.columns.map((c) => (
                    <TableCell
                      key={c}
                      className={typeof r[c] === "number" ? "tabular-nums" : ""}
                    >
                      {r[c] ?? "-"}
                    </TableCell>
                  ))}
                  <TableCell className="text-right">
                    <button
                      onClick={() => openEdit(r)}
                      className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground mr-1"
                      title="Edit"
                    >
                      <PencilSimple size={14} />
                    </button>
                    <button
                      onClick={() => remove(r)}
                      className="p-1.5 rounded-md hover:bg-destructive/15 text-muted-foreground hover:text-destructive"
                      title="Delete"
                    >
                      <Trash size={14} />
                    </button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editing ? "Edit" : "Add"} {def.label.slice(0, -1)}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {def.fields.map((f) => (
              <div key={f.name}>
                <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                  {f.label}
                  {f.required && (
                    <span className="text-destructive ml-0.5">*</span>
                  )}
                </Label>
                <Input
                  data-testid={`field-${def.key}-${f.name}`}
                  type={f.type || "text"}
                  step={f.type === "number" ? "0.01" : undefined}
                  className="mt-1.5 bg-background border-border"
                  value={form[f.name] ?? ""}
                  onChange={(e) =>
                    setForm({ ...form, [f.name]: e.target.value })
                  }
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              data-testid={`save-${def.key}`}
              onClick={save}
              loading={saving}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {editing ? "Save changes" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Masters() {
  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="masters-page">
      <header>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          Configuration
        </div>
        <h1 className="font-display text-3xl md:text-4xl tracking-tight mt-1">
          Masters
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Branches, categories, purities and live metal rates.
        </p>
      </header>

      <Tabs defaultValue="branches" className="space-y-6">
        <TabsList className="bg-secondary">
          {MASTERS.map((m) => (
            <TabsTrigger key={m.key} value={m.key} data-testid={`tab-${m.key}`}>
              {m.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {MASTERS.map((m) => (
          <TabsContent key={m.key} value={m.key}>
            <MasterTable def={m} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
